import React, { useRef, useEffect } from 'react';

const PINInput = ({ value, onChange, onComplete, disabled = false, autoFocus = false }) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, newValue) => {
    // Ne permettre que les chiffres
    if (newValue && !/^\d$/.test(newValue)) {
      return;
    }

    // Construire le nouveau PIN en remplaçant le chiffre à l'index
    const currentPin = (value || '').replace(/\D/g, '');
    const pinArray = currentPin.split('');
    // S'assurer qu'on a un tableau de 6 éléments
    while (pinArray.length < 6) {
      pinArray.push('');
    }
    pinArray[index] = newValue || '';
    const updatedPin = pinArray.join('').replace(/\D/g, '').slice(0, 6);

    onChange(updatedPin);

    // Si on a entré un chiffre, passer au champ suivant
    if (newValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Si le PIN est complet (6 chiffres exactement), appeler onComplete si fourni
    if (updatedPin.length === 6 && /^\d{6}$/.test(updatedPin) && onComplete) {
      onComplete(updatedPin);
    }
  };

  const handleKeyDown = (index, e) => {
    // Si on appuie sur Backspace et que le champ est vide, aller au champ précédent
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Si on appuie sur la flèche gauche, aller au champ précédent
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Si on appuie sur la flèche droite, aller au champ suivant
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length > 0) {
      onChange(pastedData);
      
      // Focus sur le dernier champ rempli
      const lastIndex = Math.min(pastedData.length - 1, 5);
      inputRefs.current[lastIndex]?.focus();
      
      // Si le PIN est complet, appeler onComplete si fourni
      if (pastedData.length === 6 && onComplete) {
        onComplete(pastedData);
      }
    }
  };

  return (
    <div className="flex justify-center gap-2 sm:gap-3">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength="1"
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-center text-2xl sm:text-3xl font-bold border-2 border-gray-300 rounded-xl focus:border-entrea-sky focus:ring-2 focus:ring-entrea-royal/25 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
          style={{ caretColor: 'transparent' }}
        />
      ))}
    </div>
  );
};

export default PINInput;

